import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { canEdit, getUserType } from "../permissions";
import API_BASE from "../config";

const EditPayment = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const payment = location.state?.payment;
    const userType = getUserType();
    const allowEdit = canEdit(userType, "payments");

    const [form, setForm] = useState({
        id: "", customer_name: "", customer_id: "", payment_id: "", booking_id: "",
        amount: "", status: "", payment_mode: "", payment_to: "", handover: "",
        payment_part: "", payment_for: "", received_by: "", unit: "", unit_id: "",
        project_id: "", demand_id: "", payment_create: "", payment_collect: "",
        cheque_no: "", cheque_bank: "", cheque_date: "", utr: "", comment: "",
    });
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        if (!allowEdit) { navigate(-1); return; }
        if (!payment) { navigate(-1); return; }
        setForm({
            id: payment.id || "", customer_name: payment.customer_name || "",
            customer_id: payment.customer_id || "", payment_id: payment.payment_id || "",
            booking_id: payment.booking_id || "", amount: payment.amount || "",
            status: payment.status || "", payment_mode: payment.payment_mode || "",
            payment_to: payment.payment_to || "", handover: payment.handover || "",
            payment_part: payment.payment_part || "", payment_for: payment.payment_for || "",
            received_by: payment.received_by || "", unit: payment.unit || "",
            unit_id: payment.unit_id || "", project_id: payment.project_id || "",
            demand_id: payment.demand_id || "", payment_create: payment.payment_create || "",
            payment_collect: payment.payment_collect || "", cheque_no: payment.cheque_no || "",
            cheque_bank: payment.cheque_bank || "", cheque_date: payment.cheque_date || "",
            utr: payment.utr || "", comment: payment.comment || "",
        });
    }, [payment, navigate, allowEdit]);

    if (!allowEdit) return null;

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = (e) => {
        e.preventDefault();
        setSaving(true); setError(""); setSuccess("");

        const formData = new URLSearchParams();
        Object.keys(form).forEach((key) => {
            if (key !== "id") {
                // If the payment mode is RTGS, do not send received_by
                if (key === "received_by" && form.payment_mode === "RTGS") {
                    formData.append(key, "");
                } else {
                    formData.append(key, form[key]);
                }
            }
        });

        fetch(`${API_BASE}/api/updatedatas`, {
            method: "POST",
            headers: { Authorization: "1", Table: "payments", Id: form.id },
            body: formData,
        })
            .then((r) => r.json())
            .then((json) => {
                setSaving(false);
                if (json.status === true) { setSuccess("Payment updated!"); setTimeout(() => navigate(-1), 1500); }
                else setError(json.message || "Failed to update.");
            })
            .catch(() => { setSaving(false); setError("Error updating payment."); });
    };

    return (
        <>
            <style>{`
        .ep-page{max-width:900px;margin:0 auto}
        .ep-hdr{display:flex;align-items:center;gap:14px;margin-bottom:24px}
        .ep-back{padding:8px 16px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;color:#334155;cursor:pointer;font-size:14px;font-weight:500;font-family:'Inter',sans-serif;transition:all .2s;display:flex;align-items:center;gap:6px}
        .ep-back:hover{background:#f1f5f9;border-color:#cbd5e1}
        .ep-title{font-size:22px;font-weight:700;color:#0f172a}
        .ep-sub{font-size:13px;color:#64748b}
        .ep-card{background:#fff;border:1px solid #e2e8f0;border-radius:16px;padding:28px;box-shadow:0 1px 3px rgba(0,0,0,.04)}
        .ep-sec{font-size:14px;font-weight:600;color:#0f172a;margin-bottom:16px;padding-bottom:10px;border-bottom:1px solid #f1f5f9;display:flex;align-items:center;gap:8px}
        .ep-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px;margin-bottom:28px}
        .ep-field{display:flex;flex-direction:column;gap:6px}
        .ep-field.full{grid-column:1/-1}
        .ep-field label{font-size:12px;font-weight:600;color:#64748b;text-transform:uppercase;letter-spacing:.4px}
        .ep-field input,.ep-field select,.ep-field textarea{padding:11px 14px;border-radius:10px;border:1px solid #e2e8f0;font-size:14px;font-family:'Inter',sans-serif;color:#1e293b;background:#f8fafc;outline:none;transition:all .2s}
        .ep-field input:focus,.ep-field select:focus,.ep-field textarea:focus{border-color:#93c5fd;box-shadow:0 0 0 3px rgba(59,130,246,.08);background:#fff}
        .ep-field textarea{min-height:80px;resize:vertical}
        .ep-actions{display:flex;gap:12px;margin-top:8px;padding-top:20px;border-top:1px solid #f1f5f9}
        .ep-save{padding:12px 28px;background:linear-gradient(135deg,#2563eb,#3b82f6);color:#fff;border:none;border-radius:10px;font-size:14px;font-weight:600;cursor:pointer;font-family:'Inter',sans-serif;transition:all .2s;display:flex;align-items:center;gap:8px}
        .ep-save:hover:not(:disabled){background:linear-gradient(135deg,#1d4ed8,#2563eb);box-shadow:0 4px 14px rgba(37,99,235,.3);transform:translateY(-1px)}
        .ep-save:disabled{opacity:.6;cursor:not-allowed;transform:none}
        .ep-cancel{padding:12px 24px;background:#f8fafc;color:#64748b;border:1px solid #e2e8f0;border-radius:10px;font-size:14px;font-weight:500;cursor:pointer;font-family:'Inter',sans-serif;transition:all .2s}
        .ep-cancel:hover{background:#f1f5f9;color:#334155}
        .ep-spinner{width:16px;height:16px;border:2px solid rgba(255,255,255,.3);border-top-color:#fff;border-radius:50%;animation:ep-sp .6s linear infinite}
        @keyframes ep-sp{to{transform:rotate(360deg)}}
        .ep-alert{padding:12px 16px;border-radius:10px;font-size:13px;margin-bottom:20px;font-weight:500}
        .ep-alert-ok{background:#ecfdf5;color:#059669;border:1px solid #a7f3d0}
        .ep-alert-err{background:#fef2f2;color:#dc2626;border:1px solid #fecaca}
        @media(max-width:768px){.ep-grid{grid-template-columns:1fr}.ep-card{padding:20px}.ep-actions{flex-direction:column}}
      `}</style>

            <div className="ep-page">
                <div className="ep-hdr">
                    <button className="ep-back" onClick={() => navigate(-1)}>← Back</button>
                    <div>
                        <div className="ep-title">Edit Payment</div>
                        <div className="ep-sub">Payment ID: {form.payment_id}</div>
                    </div>
                </div>

                {success && <div className="ep-alert ep-alert-ok">✅ {success}</div>}
                {error && <div className="ep-alert ep-alert-err">⚠️ {error}</div>}

                <form className="ep-card" onSubmit={handleSubmit}>
                    <div className="ep-sec">👤 Customer & Booking</div>
                    <div className="ep-grid">
                        <div className="ep-field"><label>Customer Name</label><input name="customer_name" value={form.customer_name} onChange={handleChange} /></div>
                        <div className="ep-field"><label>Booking ID</label><input name="booking_id" value={form.booking_id} onChange={handleChange} /></div>
                        <div className="ep-field"><label>Unit</label><input name="unit" value={form.unit} onChange={handleChange} /></div>
                    </div>

                    <div className="ep-sec">💵 Payment Info</div>
                    <div className="ep-grid">
                        <div className="ep-field"><label>Amount (₹)</label><input name="amount" type="number" value={form.amount} onChange={handleChange} /></div>
                        <div className="ep-field">
                            <label>Status</label>
                            <select name="status" value={form.status} onChange={handleChange}>
                                <option value="">Select</option>
                                <option value="Confirmed">Confirmed</option>
                                <option value="Received">Received</option>
                            </select>
                        </div>
                        <div className="ep-field">
                            <label>Payment Mode</label>
                            <select name="payment_mode" value={form.payment_mode} onChange={handleChange}>
                                <option value="">Select</option>
                                <option value="Cash">Cash</option>
                                <option value="Bank Transfer">Bank Transfer</option>
                                <option value="Bank">Bank</option>
                                <option value="Cheque/DD">Cheque/DD</option>
                                <option value="Cheque /DD">Cheque / DD</option>
                                <option value="Phonepe/UPI">Phonepe/UPI</option>
                                <option value="Phone Pe/ Google">Phone Pe/ Google</option>
                            </select>
                        </div>
                        <div className="ep-field"><label>Payment To</label><input name="payment_to" value={form.payment_to} onChange={handleChange} /></div>
                        <div className="ep-field">
                            <label>Payment Part</label>
                            <select name="payment_part" value={form.payment_part} onChange={handleChange}>
                                <option value="">Select</option>
                                <option value="Self">Self</option>
                                <option value="Bank Loan">Bank Loan</option>
                            </select>
                        </div>
                        <div className="ep-field"><label>Payment For</label><input name="payment_for" value={form.payment_for} onChange={handleChange} /></div>
                        {form.payment_mode !== "RTGS" && (
                            <div className="ep-field"><label>Received By</label><input name="received_by" value={form.received_by} onChange={handleChange} /></div>
                        )}
                        <div className="ep-field"><label>Collection Date</label><input name="payment_collect" value={form.payment_collect} onChange={handleChange} /></div>
                        <div className="ep-field"><label>Handover</label><input name="handover" value={form.handover} onChange={handleChange} /></div>
                    </div>

                    <div className="ep-sec">🏦 Cheque / UTR Details</div>
                    <div className="ep-grid">
                        <div className="ep-field"><label>Cheque No</label><input name="cheque_no" value={form.cheque_no} onChange={handleChange} /></div>
                        <div className="ep-field"><label>Cheque Bank</label><input name="cheque_bank" value={form.cheque_bank} onChange={handleChange} /></div>
                        <div className="ep-field"><label>Cheque Date</label><input name="cheque_date" value={form.cheque_date} onChange={handleChange} /></div>
                        <div className="ep-field"><label>UTR Number</label><input name="utr" value={form.utr} onChange={handleChange} /></div>
                    </div>

                    <div className="ep-sec">💬 Notes</div>
                    <div className="ep-field full" style={{ marginBottom: 20 }}>
                        <label>Comment</label>
                        <textarea name="comment" value={form.comment} onChange={handleChange} placeholder="Add notes..." />
                    </div>

                    <div className="ep-actions">
                        <button type="submit" className="ep-save" disabled={saving}>
                            {saving && <span className="ep-spinner" />}
                            {saving ? "Saving..." : "💾 Save Changes"}
                        </button>
                        <button type="button" className="ep-cancel" onClick={() => navigate(-1)}>Cancel</button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default EditPayment;
